import type { AstNode, AstGroup } from '../types';
import { NormalizedDataSource } from './datasource.types';
import { SupportedTools } from '../../../interfaces';
import { resolveDatasource } from './datasource.resolve';

export type Joiner = (prelude: string, body: string) => string;

/**
 * Alternative to the prelude+join pattern for tools where the body must be
 * wrapped (e.g. SQL SELECT...FROM...WHERE). When provided to
 * wrapRendererWithDatasource, emitPrelude and join are unused.
 */
export type BodyWrapper = (body: string, ds: NormalizedDataSource | undefined) => string;

// Common joiners per tool style
export const joinWithAnd: Joiner = (p, b) => `${p} AND ${b}`;
export const joinWithPipeBlock: Joiner = (p, b) => `${p}\n${b}`;
/** Concatenate prelude directly to body (body provides its own leading separator, e.g. " AND ") */
export const joinDirect: Joiner = (p, b) => `${p}${b}`;

/** Pull datasource inputs from AST.directives in a consistent way. */
export function parseDatasourceInputs(ast: AstNode): {
  categories: string[];
  platforms?: string[];
  logCategory?: string;
  overrideEvents?: string[];
  overrideEventCodes?: string[];
  overridePlatformClauseList?: string[];
  overrideRepo?: string;
  overrideRepoSubtype?: string;
  overrideEventProvider?: string;
  overrideSelectFields?: string[];
} {
  const root = ast.kind === 'group' ? (ast as AstGroup) : undefined;
  const ds = root?.directives?.datasource;

  const catRaw = ds?.category;
  const platRaw = ds?.platform;

  const categories = Array.isArray(catRaw)
    ? (catRaw as string[])
    : catRaw
      ? [catRaw as string]
      : [];

  const platforms = Array.isArray(platRaw)
    ? (platRaw as string[])
    : platRaw
      ? [platRaw as string]
      : undefined;

  return {
    categories,
    platforms,
    logCategory: ds?.logCategory,
    overrideEvents: ds?.events?.length ? ds.events : undefined,
    overrideEventCodes: ds?.eventCodes?.length ? ds.eventCodes : undefined,
    overridePlatformClauseList: ds?.platformClauseList?.length ? ds.platformClauseList : undefined,
    overrideRepo: ds?.repo,
    overrideRepoSubtype: ds?.repoSubtype,
    overrideEventProvider: ds?.eventProvider,
    overrideSelectFields: ds?.selectFields?.length ? ds.selectFields : undefined,
  };
}

/** Internal: resolve datasource from map or build a minimal shell. */
function resolveDs(
  tool: SupportedTools,
  categories: string[],
  platforms: string[] | undefined,
): NormalizedDataSource {
  return categories.length
    ? (resolveDatasource(tool, platforms, categories) ?? {
        categories,
        platform: platforms,
        events: [],
        eventCodes: [],
        keywords: [],
        extra: {},
      })
    : { categories: [], platform: platforms, events: [], eventCodes: [], keywords: [], extra: {} };
}

/** Internal: apply directive-level overrides onto a resolved NormalizedDataSource. */
function applyOverrides(
  ds: NormalizedDataSource,
  o: {
    overrideEvents?: string[];
    overrideEventCodes?: string[];
    overridePlatformClauseList?: string[];
    overrideRepo?: string;
    overrideRepoSubtype?: string;
    overrideEventProvider?: string;
    overrideSelectFields?: string[];
  },
): NormalizedDataSource {
  let out = ds;
  if (o.overrideEvents) out = { ...out, events: o.overrideEvents };
  if (o.overrideEventCodes) out = { ...out, eventCodes: o.overrideEventCodes };
  if (o.overridePlatformClauseList)
    out = {
      ...out,
      extra: { ...(out.extra ?? {}), platformClauseList: o.overridePlatformClauseList },
    };
  if (o.overrideRepo !== undefined) out = { ...out, repo: o.overrideRepo };
  if (o.overrideRepoSubtype !== undefined) out = { ...out, repoSubtype: o.overrideRepoSubtype };
  if (o.overrideEventProvider !== undefined)
    out = { ...out, eventProvider: o.overrideEventProvider };
  if (o.overrideSelectFields) out = { ...out, selectFields: o.overrideSelectFields };
  return out;
}

/**
 * Wrap a base renderer so it auto-prepends a datasource prelude (if resolvable).
 * - `emitPrelude` is tool-specific (Splunk vs LogScale).
 * - `join` controls how to stitch prelude + base (AND vs newline, etc).
 * - `wrapBody` is an alternative to emitPrelude+join for SQL-style tools (QRadar).
 *   When provided, emitPrelude and join are unused; wrapBody(renderedBody, resolvedDs) is called.
 */
export function wrapRendererWithDatasource<R extends (ast: AstNode) => string>(
  base: R,
  tool: SupportedTools,
  emitPrelude: ((ds: NormalizedDataSource) => string) | null,
  join: Joiner | null,
  helpText?: string,
  wrapBody?: BodyWrapper,
): R {
  const wrapped = ((ast: AstNode) => {
    const body = base(ast);

    const {
      categories,
      platforms,
      logCategory,
      overrideEvents,
      overrideEventCodes,
      overridePlatformClauseList,
      overrideRepo,
      overrideRepoSubtype,
      overrideEventProvider,
      overrideSelectFields,
    } = parseDatasourceInputs(ast);

    const hasAnything =
      categories.length ||
      platforms?.length ||
      logCategory !== undefined ||
      overrideEvents?.length ||
      overrideEventCodes?.length ||
      overridePlatformClauseList?.length ||
      overrideRepo !== undefined ||
      overrideRepoSubtype !== undefined ||
      overrideEventProvider !== undefined ||
      overrideSelectFields !== undefined;

    // BodyWrapper path: always call wrapBody when provided, even without datasource directives
    if (wrapBody) {
      if (!hasAnything) return wrapBody(body, undefined);
      let ds = resolveDs(tool, categories, platforms);
      ds = applyOverrides(ds, {
        overrideEvents,
        overrideEventCodes,
        overridePlatformClauseList,
        overrideRepo,
        overrideRepoSubtype,
        overrideEventProvider,
        overrideSelectFields,
      });
      if (logCategory) ds = { ...ds, logCategory };
      return wrapBody(body, ds);
    }

    if (!hasAnything) return body;

    let ds = resolveDs(tool, categories, platforms);
    ds = applyOverrides(ds, {
      overrideEvents,
      overrideEventCodes,
      overridePlatformClauseList,
      overrideRepo,
      overrideRepoSubtype,
      overrideEventProvider,
      overrideSelectFields,
    });
    if (logCategory) ds = { ...ds, logCategory };

    const prelude = emitPrelude!(ds);
    return prelude ? join!(prelude, body) : body;
  }) as R;

  // preserve help text for UI
  (wrapped as any).helpText = helpText ?? (base as any).helpText;
  (wrapped as any).datasourceHelpText = (base as any).datasourceHelpText;
  (wrapped as any).dataDisplayHelpText = (base as any).dataDisplayHelpText;
  (wrapped as any).makeComment = (base as any).makeComment;
  return wrapped;
}
